<template>
  <div class="component-wrapper">
    <b-card class="main-content">
      <template #header>
        <!-- View Toggle for ADM users only -->
        <div v-if="user.roleCode === 'ADM'">
          <b-button-group size="sm">
            <b-button :variant="viewMode === 'admin' ? 'primary' : 'outline-primary'" @click="switchViewMode('admin')">
              <feather-icon icon="ShieldIcon" size="14" class="mr-50" />
              Admin Users
            </b-button>
            <b-button
              :variant="viewMode === 'clients' ? 'primary' : 'outline-primary'"
              @click="switchViewMode('clients')"
            >
              <feather-icon icon="UsersIcon" size="14" class="mr-50" />
              Client Users
            </b-button>
          </b-button-group>
        </div>

        <!-- Client Selector for Client View (Desktop) -->
        <div
          v-if="(user.roleCode === 'ADM' && viewMode === 'clients') || user.roleCode === 'SALES'"
          class="d-none d-sm-none d-md-block ml-auto"
          style="min-width: 300px; max-width: 400px;"
        >
          <v-select
            v-model="selectedClient"
            :options="clients"
            label="description"
            :reduce="client => client.code"
            placeholder="-- Select a client --"
            @input="onClientChange"
          />
        </div>

        <!-- Client Selector for Client View (Mobile) -->
        <div
          v-if="(user.roleCode === 'ADM' && viewMode === 'clients') || user.roleCode === 'SALES'"
          class="d-block d-sm-block d-md-none mt-1"
        >
          <v-select
            v-model="selectedClient"
            :options="clients"
            label="description"
            :reduce="client => client.code"
            placeholder="-- Select a client --"
            @input="onClientChange"
          />
        </div>

        <create-button
          v-if="user.roleCode === 'ADM' && viewMode === 'admin'"
          text="Add User Account"
          class="d-none d-sm-none d-md-block ml-auto"
          @click.stop="showDetail(null)"
        ></create-button>
        <create-button
          v-if="user.roleCode === 'ADM' && viewMode === 'admin'"
          text="Add User Account"
          class="d-block d-sm-block d-md-none"
          :block="true"
          @click.stop="showDetail(null)"
        ></create-button>
      </template>
      <template #default>
        <vue-element-loading :active="loader" />

        <!-- ADMIN VIEW: Simple Table (for ADM when viewMode='admin' or non-ADM/non-SALES users) -->
        <admin-view
          v-if="
            (user.roleCode !== 'ADM' && user.roleCode !== 'SALES') || (user.roleCode === 'ADM' && viewMode === 'admin')
          "
          :items="table.items"
          :current-user="user"
          :paging="table.paging"
          :filter="table.filter"
          @edit="showDetail"
          @reset-password="resetPassword"
          @toggle-active="toggleActive"
        />

        <!-- CLIENT VIEW: Users Table (for ADM when viewMode='clients' OR for SALES role) -->
        <client-view
          v-else-if="(user.roleCode === 'ADM' && viewMode === 'clients') || user.roleCode === 'SALES'"
          :selected-client="selectedClient"
          :selected-client-object="selectedClientObject"
          :client-users="clientUsers"
          :current-user="user"
          :paging="clientTable.paging"
          @edit="showDetail"
          @reset-password="resetPassword"
          @toggle-active="toggleActive"
        />
      </template>

      <template #footer>
        <div class="d-none d-sm-none d-md-block">
          <div class="d-flex mt-auto align-items-center">
            <page-info :page-no="table.paging.pageNo" :page-size="table.paging.pageSize" :total="table.items.length" />
            <b-pagination
              v-model="table.paging.pageNo"
              :totalRows="table.items.length"
              :per-page="table.paging.pageSize"
              align="center"
              class="ml-auto"
            ></b-pagination>
          </div>
        </div>
        <div class="d-xs-block d-sm-block d-md-none">
          <b-pagination
            v-model="table.paging.pageNo"
            :totalRows="table.items.length"
            :per-page="table.paging.pageSize"
            align="center"
          ></b-pagination>
          <page-info
            :page-no="table.paging.pageNo"
            :page-size="table.paging.pageSize"
            :total="table.items.length"
            class="text-center"
          />
        </div>
      </template>
    </b-card>
    <b-modal
      v-model="modal"
      :title="!table.selected ? `Add Administrator` : `Edit Administrator`"
      hide-footer
      centered
      no-close-on-backdrop
      size="lg"
      lazy
      @hide="table.selected = null"
    >
      <detail :userCode="table.selected" @onSaveSuccess="saveSuccess" @onCancel="cancel" />
    </b-modal>
  </div>
</template>

<script>
import api from "@api";
import Detail from "./component/Detail.vue";
import AdminView from "./component/AdminView.vue";
import ClientView from "./component/ClientView.vue";
import { mapState } from "vuex";

export default {
  name: `UserAccountList`,
  components: { Detail, AdminView, ClientView },
  data() {
    return {
      viewMode: "admin", // 'admin' or 'clients' (for ADM only)
      clients: [], // list of all clients
      selectedClient: undefined, // selected client code
      clientUsers: [], // users for selected client
      table: {
        items: [],
        paging: {
          pageNo: 1,
          pageSize: 20,
        },
        filter: null,
        selected: null,
      },
      clientTable: {
        paging: {
          pageNo: 1,
          pageSize: 20,
        },
      },
      loader: false,
      modal: false,
    };
  },
  computed: {
    ...mapState(`auth`, [`user`]),
    selectedClientObject() {
      if (!this.selectedClient || !this.clients.length) return null;
      return this.clients.find(c => c.code === this.selectedClient) || null;
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    switchViewMode(mode) {
      this.viewMode = mode;
      this.loadData();
    },

    showDetail(userCode) {
      this.table.selected = userCode;
      this.modal = true;
    },

    async loadData() {
      this.loader = true;
      try {
        if (this.user.roleCode === `ADM`) {
          if (this.viewMode === "admin") {
            // Load admin/sales users only
            this.table.items = await api.get(`useraccount/admin`);
            this.clients = [];
            this.selectedClient = undefined;
            this.clientUsers = [];
          } else {
            // Load all clients for selection
            this.clients = await api.get(`client`);
            this.table.items = [];
            this.selectedClient = undefined;
            this.clientUsers = [];
          }
        } else if (this.user.roleCode === `SALES`) {
          // Sales sees only their assigned clients
          this.clients = await api.get(`client/by-sales/${this.user.userCode}`);
          this.table.items = [];
          this.selectedClient = undefined;
          this.clientUsers = [];
        } else {
          // Client users see users from their own client
          this.table.items = await api.get(`useraccount/client/${this.user.clientCode}`);
        }
      } catch (error) {
        this.showToast(`error`, error);
      } finally {
        this.loader = false;
      }
    },

    async onClientChange() {
      if (!this.selectedClient) {
        this.clientUsers = [];
        return;
      }

      this.loader = true;
      try {
        // Load users for the selected client
        this.clientUsers = await api.get(`useraccount/client/${this.selectedClient}`);
        // Reset pagination
        this.clientTable.paging.pageNo = 1;
      } catch (error) {
        this.showToast(`error`, error);
        this.clientUsers = [];
      } finally {
        this.loader = false;
      }
    },

    async saveSuccess() {
      this.showToast(`success`, `User Account save success`);
      this.modal = false;
      this.loadData();
    },

    async resetPassword(userCode) {
      let confirmed = await this.showConfirm(`Reset Password ?`);
      if (!confirmed) return;
      this.loader = true;
      try {
        await api.put(`useraccount/reset-password/${userCode}`);
        this.showToast(`success`, `Password has been reset`);
      } catch (error) {
        this.showToast(`error`, error);
      } finally {
        this.loader = false;
      }
    },

    async toggleActive(rec) {
      let confirmed = await this.showConfirm(`Toggle Active ?`);
      if (!confirmed) return;
      this.loader = true;
      try {
        await api.delete(`useraccount/${rec.userCode}`);
        rec.isActive = !rec.isActive;
        // Reload data to reflect changes in both views
        await this.loadData();
      } catch (error) {
        this.showToast(`error`, error);
      } finally {
        this.loader = false;
      }
    },

    cancel() {
      this.modal = false;
    },
  },
};
</script>
